<template>
  <v-flex xs12 class="netx-panel px-0 mx-0 mb-4">
    <div class="netx-header">
      <div>
        <div class="netx-title">{{ panelTitle }}</div>
        <div v-if="context === 'public'" class="netx-description">Choose images for the public gallery and its top image.</div>
        <div class="netx-meta" v-if="!loading">
          {{ total }} image<span v-if="total !== 1">s</span>
          <span v-if="metadataDifferenceCount" class="netx-difference-count">
            · {{ metadataDifferenceCount }} image<span v-if="metadataDifferenceCount !== 1">s</span> differ
          </span>
        </div>
      </div>
      <div class="netx-actions">
        <span v-if="syncMessage" class="netx-sync-message" :class="syncMessageClass">{{ syncMessage }}</span>
        <v-text-field
          v-if="showSearch"
          v-model="search"
          append-icon="search"
          label="Search cached NetX"
          single-line
          hide-details
          clearable
          class="netx-search"
          @click:clear="clearSearch"
        />
        <v-checkbox v-if="context !== 'public'" v-model="searchAll" label="All" hide-details class="netx-all" />
        <v-btn
          v-if="canSyncNetx"
          small
          color="primary"
          :loading="syncActive"
          :disabled="syncActive || selectionBusy || !artwork.accession_number"
          @click="syncFromNetx"
        ><v-icon small left>cloud_download</v-icon>Sync from NetX</v-btn>
        <v-btn icon small :loading="loading" title="Refresh NetX images" @click="loadAssets"><v-icon small>refresh</v-icon></v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" outline class="mx-3 my-3">{{ error }}</v-alert>
    <v-alert v-if="message" type="success" outline class="mx-3 my-3">{{ message }}</v-alert>
    <v-progress-linear v-if="loading" indeterminate height="2" class="ma-0" />
    <div v-if="!loading && assets.length === 0" class="netx-empty">No NetX images found.</div>

    <v-container v-else grid-list-md fluid class="netx-grid">
      <v-layout row wrap>
        <v-flex v-for="asset in assets" :key="asset.id" xs12 sm6 md4 lg3>
          <netx-asset-card
            :artwork="artwork"
            :asset="asset"
            :disabled="selectionBusy"
            :context="context"
            @select="setSelection"
            @retry="mediaRetryStarted"
            @notice="error = $event"
            @edit="editPhoto"
          />
        </v-flex>
      </v-layout>
    </v-container>
    <artwork-photo-drawer
      v-model="photoDrawerOpen"
      :artwork="artwork"
      :photo-id="photoDrawerPhotoId"
      @saved="photoSaved"
    />
  </v-flex>
</template>

<script>
import NetxAssetCard from './NetxAssetCard'
import ArtworkPhotoDrawer from './ArtworkPhotoDrawer'
import {
  artworkImportError,
  canSyncNetx,
  failedSelection,
  loadNetxAssets,
  loadNetxImportRun,
  pendingSelection,
  selectionError,
  startNetxArtworkImport,
  updateNetxSelection
} from '@/utils/netxArtworkReview'
import { mediaActive } from '@/utils/artworkMedia'

const MIN_SEARCH_LENGTH = 3
const SEARCH_DEBOUNCE_MS = 250
const MEDIA_POLL_INTERVAL_MS = 5000

export default {
  props: {
    artwork: { type: Object, required: true },
    context: { type: String, default: 'images' }
  },
  components: { NetxAssetCard, ArtworkPhotoDrawer },
  data: () => ({
    assets: [],
    total: 0,
    artworkAssetCount: 0,
    loading: false,
    error: '',
    message: '',
    search: '',
    searchAll: false,
    requestSequence: 0,
    lastLoadedQuery: '',
    searchTimer: null,
    pollingTimer: null,
    syncPollingTimer: null,
    syncArtworkId: null,
    syncState: {},
    photoDrawerOpen: false,
    photoDrawerPhotoId: null
  }),
  watch: {
    'artwork.id': function () {
      window.clearTimeout(this.syncPollingTimer)
      this.syncArtworkId = null
      this.syncState = {}
      this.requestSequence += 1
      this.search = ''
      this.artworkAssetCount = 0
      this.lastLoadedQuery = ''
      if (this.searchAll) {
        this.searchAll = false
      } else {
        this.loadAssets()
      }
    },
    searchAll: function () { this.loadAssets() },
    search: function () {
      window.clearTimeout(this.searchTimer)
      const query = this.searchQuery(this.search)
      if (query === this.lastLoadedQuery) return

      this.searchTimer = window.setTimeout(
        () => this.loadAssets(),
        query ? SEARCH_DEBOUNCE_MS : 0
      )
    }
  },
  computed: {
    panelTitle () {
      return this.context === 'public' ? 'NetX public image candidates' : 'NetX images'
    },
    showSearch () {
      return this.artworkAssetCount > 10
    },
    selectionBusy () {
      return this.assets.some(asset => this.selectionActive(asset) || mediaActive(asset))
    },
    metadataDifferenceCount () {
      return this.assets.filter(asset => asset.artwork_photo_metadata_differs).length
    },
    canSyncNetx () {
      return canSyncNetx(this.c_user)
    },
    syncActive () {
      return ['pending', 'running'].includes(this.syncState.status)
    },
    syncMessage () {
      if (this.syncState.status === 'completed') {
        return `Synced: ${this.syncState.assets_created || 0} new, ${this.syncState.assets_updated || 0} updated, ${this.syncState.assets_failed || 0} failed`
      }
      if (this.syncState.status === 'failed') return this.syncState.error_message || 'NetX sync failed.'
      if (this.syncState.status === 'pending') return 'NetX sync queued'
      if (this.syncState.status === 'running') return 'Syncing NetX images'
      return ''
    },
    syncMessageClass () {
      return this.syncState.status === 'failed' ? 'red--text' : ''
    }
  },
  methods: {
    searchQuery (value) {
      const query = String(value || '').trim()
      return query.length >= MIN_SEARCH_LENGTH ? query : ''
    },
    clearSearch () {
      this.search = ''
    },
    editPhoto ({ photoId }) {
      this.photoDrawerPhotoId = photoId
      this.photoDrawerOpen = true
    },
    photoSaved (result) {
      this.message = result.publicProcessingQueued
        ? 'Image details saved. Public image versions are processing.'
        : 'Image details saved.'
      this.loadAssets({ silent: true })
      this.$emit('updated')
    },
    async loadAssets (options) {
      if (!this.artwork || !this.artwork.id) return
      const silent = options && options.silent
      const requestSequence = ++this.requestSequence
      if (!silent) this.loading = true
      this.error = ''
      if (!silent) this.message = ''
      const params = {}
      if (!this.searchAll) params.artwork_id = this.artwork.id
      const query = this.searchQuery(this.search)
      if (query) params.q = query

      const hadActiveSelection = this.assets.some(asset => this.selectionActive(asset))
      try {
        const assets = await loadNetxAssets(params)
        if (requestSequence !== this.requestSequence) return
        this.assets = assets
        this.total = assets.length
        this.lastLoadedQuery = query
        if (!query && !this.searchAll) this.artworkAssetCount = this.total
        if (hadActiveSelection && !this.assets.some(asset => this.selectionActive(asset))) {
          this.$emit('updated')
        }
        this.schedulePolling()
      } catch (error) {
        if (requestSequence !== this.requestSequence) return
        console.error('NetX images failed to load', error)
        this.error = 'NetX images could not be loaded.'
      } finally {
        if (!silent && requestSequence === this.requestSequence) this.loading = false
      }
    },
    setSelection ({ asset, desired }) {
      if (this.selectionBusy || !asset.promotable) return
      this.$set(asset, 'selection', pendingSelection(asset, desired))

      updateNetxSelection(this.artwork.id, asset.id, desired).then(response => {
        this.$set(asset, 'selection', response.data)
        this.schedulePolling()
      }).catch(error => {
        console.error('NetX selection update failed', error)
        const message = selectionError(error)
        this.$set(asset, 'selection', failedSelection(asset, message))
        this.error = message
      })
    },
    mediaRetryStarted () {
      this.error = ''
      this.loadAssets({ silent: true })
    },
    async syncFromNetx () {
      if (!this.canSyncNetx || this.syncActive || !this.artwork.accession_number) return
      const artworkId = this.artwork.id
      this.syncArtworkId = artworkId
      this.syncState = { status: 'pending', pollFailures: 0 }

      try {
        const response = await startNetxArtworkImport(artworkId)
        if (this.syncArtworkId !== artworkId) return
        this.syncState = Object.assign({}, response.data, { pollFailures: 0 })
        this.scheduleSyncPolling(response.data.id, null, artworkId)
      } catch (error) {
        if (this.syncArtworkId !== artworkId) return
        this.syncState = { status: 'failed', error_message: artworkImportError(error) }
      }
    },
    scheduleSyncPolling (importRunId, delay, artworkId) {
      window.clearTimeout(this.syncPollingTimer)
      const expectedArtworkId = artworkId || this.syncArtworkId
      this.syncPollingTimer = window.setTimeout(() => this.pollSync(importRunId, expectedArtworkId), delay || 1200)
    },
    async pollSync (importRunId, artworkId) {
      if (this.syncArtworkId !== artworkId || Number(this.artwork.id) !== Number(artworkId)) return
      const previous = this.syncState
      try {
        const response = await loadNetxImportRun(importRunId)
        if (this.syncArtworkId !== artworkId || Number(this.artwork.id) !== Number(artworkId)) return
        this.syncState = Object.assign({}, response.data, { pollFailures: 0 })
        if (this.syncActive) {
          this.scheduleSyncPolling(importRunId, null, artworkId)
          return
        }
        if (this.syncState.status === 'completed') await this.loadAssets({ silent: true })
      } catch (error) {
        const pollFailures = (previous.pollFailures || 0) + 1
        if (pollFailures < 3) {
          this.syncState = Object.assign({}, previous, { pollFailures })
          this.scheduleSyncPolling(importRunId, 2200, artworkId)
          return
        }
        this.syncState = { status: 'failed', error_message: 'NetX sync status could not be refreshed.' }
      }
    },
    schedulePolling () {
      window.clearTimeout(this.pollingTimer)
      if (!this.assets.some(asset => this.selectionActive(asset) || mediaActive(asset))) return
      this.pollingTimer = window.setTimeout(() => this.loadAssets({ silent: true }), MEDIA_POLL_INTERVAL_MS)
    },
    selectionActive (asset) { return !!(asset.selection && asset.selection.active) }
  },
  mounted () { this.loadAssets() },
  beforeDestroy () {
    window.clearTimeout(this.pollingTimer)
    window.clearTimeout(this.searchTimer)
    window.clearTimeout(this.syncPollingTimer)
  }
}
</script>

<style scoped>
.netx-panel { border: 1px solid #d8d8d8; background: #f7f7f5; }
.netx-header { align-items: center; background: #101010; color: #f3f3f0; display: flex; justify-content: space-between; min-height: 58px; padding: 10px 14px; }
.netx-title { font-size: 16px; font-weight: 700; letter-spacing: 0; }
.netx-meta { color: #b8b8b2; font-size: 12px; line-height: 1.3; }
.netx-difference-count { color: #ffc46b; }
.netx-description { color: #b8b8b2; font-size: 12px; line-height: 1.3; margin-top: 2px; }
.netx-actions { align-items: center; display: flex; max-width: 560px; min-width: 280px; }
.netx-sync-message { color: #b8b8b2; flex: 0 1 auto; font-size: 11px; margin-right: 8px; }
.netx-search { background: #fff; border-radius: 2px; margin-right: 10px; max-width: 300px; padding: 0 8px; }
.netx-all { color: #fff; flex: 0 0 auto; margin-right: 4px; }
.netx-grid { padding: 12px; }
.netx-empty { color: #666; font-size: 13px; padding: 20px 14px; }
@media (max-width: 700px) {
  .netx-header { align-items: stretch; flex-direction: column; }
  .netx-actions { margin-top: 8px; max-width: none; min-width: 0; }
  .netx-search { max-width: none; }
}
</style>



// WEBPACK FOOTER //
// src/components/artworks/NetxImages.vue